import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Collapse,
  Tabs,
  Tab,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ButtonDeleteIcon } from "@ippon/shared-assets";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useDispatch, useSelector } from "react-redux";
import {
  useForm,
  type UseFormRegister,
  type FieldErrors,
} from "react-hook-form";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CustomSectionTitle,
  CustomButton,
  CustomTextInput,
  CustomModal,
  CustomLoading,
  FixedActionBar,
  colors,
  CustomFormNavigationGuard,
} from "@ippon/shared-components";
import ManagementTaskCustomFormSection from "./ManagementTaskCustomFormSection";
import ExpenseCustomFormSection from "./ExpenseCustomFormSection";
import { Can } from "@/components/auth/Can";
import {
  type CustomFieldType,
  type UnitCustomField,
  type UnitCustomFieldSection,
} from "@ippon/shared-types";
import { store, type AppDispatch, type RootState } from "@/store";
import {
  fetchUnitCustomFields,
  createUnitCustomField,
  updateUnitCustomField,
  deleteUnitCustomField,
} from "@/store/unitCustomFieldSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import type { ApiErrorPayload, ApiErrorType } from "@/utils/apiErrorPayload";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";

// ── セクション定義 (固定項目は BasicSection / DetailSection / UnitOtherDocuments の label に合わせる)
//
// 詳細情報セクションは場所区分ごとに表示される固定フィールドが異なるが、
// カスタム項目は「共通情報」「詳細情報」のセクション単位で管理する。
interface SectionDef {
  key: UnitCustomFieldSection;
  name: string;
  fixedFields: { id: string; label: string; required: boolean }[];
}

const SECTIONS: SectionDef[] = [
  {
    key: "basic",
    name: "共通情報",
    fixedFields: [
      { id: "area_type", label: "場所区分", required: true },
      { id: "landmark_name", label: "名称", required: true },
      { id: "revenue_type", label: "収益カテゴリ", required: true },
      { id: "manager_employee_id", label: "管理責任者", required: false },
      { id: "notes", label: "備考", required: false },
    ],
  },
  {
    key: "detail",
    name: "詳細情報",
    fixedFields: [
      // 専有部
      { id: "private_detail.floor", label: "階数 (専有部)", required: false },
      {
        id: "private_detail.area_sqm",
        label: "面積 (専有部)",
        required: false,
      },
      {
        id: "private_detail.area_tsubo",
        label: "坪数 (専有部)",
        required: false,
      },
      {
        id: "private_detail.ceiling_height_m",
        label: "天井高 (専有部)",
        required: false,
      },
      {
        id: "private_detail.direction",
        label: "向き (専有部)",
        required: false,
      },
      {
        id: "private_detail.equipment_specs",
        label: "設置情報 (専有部)",
        required: false,
      },
      {
        id: "private_detail.utility_collection",
        label: "光熱費徴収 (専有部)",
        required: false,
      },
      // 共用部
      {
        id: "common_detail.usage_description",
        label: "利用用途 (共用部)",
        required: false,
      },
      { id: "common_detail.floor", label: "階数 (共用部)", required: false },
      {
        id: "common_detail.area_sqm",
        label: "面積 (共用部)",
        required: false,
      },
      {
        id: "common_detail.area_tsubo",
        label: "坪数 (共用部)",
        required: false,
      },
      {
        id: "common_detail.ceiling_height_m",
        label: "天井高 (共用部)",
        required: false,
      },
      {
        id: "common_detail.direction",
        label: "向き (共用部)",
        required: false,
      },
      {
        id: "common_detail.equipment_specs",
        label: "設置情報 (共用部)",
        required: false,
      },
      {
        id: "common_detail.documents",
        label: "関連資料 (共用部)",
        required: false,
      },
      // 建物設備
      {
        id: "facility_detail.manufacturer",
        label: "メーカー (建物設備)",
        required: false,
      },
      {
        id: "facility_detail.model_number",
        label: "型番・品番 (建物設備)",
        required: false,
      },
      {
        id: "facility_detail.manufactured_at",
        label: "製造年月 (建物設備)",
        required: false,
      },
      {
        id: "facility_detail.installed_at",
        label: "設置年月 (建物設備)",
        required: false,
      },
      {
        id: "facility_detail.capacity",
        label: "定格・容量 (建物設備)",
        required: false,
      },
      {
        id: "facility_detail.useful_life_years",
        label: "耐用年数 (建物設備)",
        required: false,
      },
      {
        id: "facility_detail.next_replacement_at",
        label: "次回交換予定年月 (建物設備)",
        required: false,
      },
      {
        id: "facility_detail.documents",
        label: "関連資料 (建物設備)",
        required: false,
      },
      // 敷地
      { id: "land_detail.land_type", label: "種別 (敷地)", required: true },
      {
        id: "land_detail.lot_number",
        label: "車室番号 (敷地/駐車場)",
        required: false,
      },
      {
        id: "land_detail.parking_subtype",
        label: "駐車場種別 (敷地/駐車場)",
        required: false,
      },
      {
        id: "land_detail.height_limit_m",
        label: "車高制限 (敷地/駐車場)",
        required: false,
      },
      {
        id: "land_detail.width_limit_m",
        label: "車幅制限 (敷地/駐車場)",
        required: false,
      },
      {
        id: "land_detail.paving_type",
        label: "舗装種別 (敷地/外構・緑地)",
        required: false,
      },
      {
        id: "land_detail.last_repair_at",
        label: "最終補修年月 (敷地/外構・緑地)",
        required: false,
      },
    ],
  },
  // 添付書類セクションはユニット詳細側にカスタム項目をレンダリングする実装が無く、
  // 設定しても画面に反映されないため一旦非表示にする。
];

// 今回はテキスト型のみサポート (種別セレクトは UI 上提供しない)。
// 将来 select / number / date 等を追加する際は再度プルダウン UI を復活させる。

// 物件一覧と同じスタイルプリセット (角丸 10px / border #d9d9d9)
const ACCORDION_SX = {
  border: "1px solid #d9d9d9",
  borderRadius: "10px",
  boxShadow: "none",
  "&:before": { display: "none" },
  "&.Mui-expanded": { borderRadius: "10px" },
} as const;

const ACCORDION_SUMMARY_SX = {
  px: 2,
  py: 0,
  minHeight: 50,
  borderRadius: "10px",
  "& .MuiAccordionSummary-content": { margin: "7px 0" },
  "&.Mui-expanded": { borderRadius: "10px 10px 0 0", minHeight: 50 },
  "&.Mui-expanded .MuiAccordionSummary-content": { margin: "7px 0" },
} as const;

interface DraftField {
  field_id: string;
  label: string;
  field_type: CustomFieldType;
  options: string;
  dirty: boolean;
  /** ローカル新規 (まだ BE 未保存)。保存時に createUnitCustomField を呼ぶ。 */
  isNew?: boolean;
  /** ローカル新規行が属するセクション (isNew=true の時のみ使用) */
  section_key?: UnitCustomFieldSection;
}

// 縦方向のみに動きを制限 (横移動を無効化)
const restrictToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});

// 親コンテナ (DndContext を囲む要素) の上下端を超えないように制限
const restrictToParentElement: Modifier = ({
  containerNodeRect,
  draggingNodeRect,
  transform,
}) => {
  if (!draggingNodeRect || !containerNodeRect) return transform;
  const next = { ...transform };
  if (draggingNodeRect.top + transform.y < containerNodeRect.top) {
    next.y = containerNodeRect.top - draggingNodeRect.top;
  }
  if (
    draggingNodeRect.bottom + transform.y >
    containerNodeRect.top + containerNodeRect.height
  ) {
    next.y =
      containerNodeRect.top +
      containerNodeRect.height -
      draggingNodeRect.bottom;
  }
  return next;
};

// ── Sortable な行コンポーネント (@dnd-kit/sortable) ─────
interface SortableFieldRowProps {
  fieldId: string;
  labelRegister: UseFormRegister<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
  onDelete: () => void;
}

const SortableFieldRow: React.FC<SortableFieldRowProps> = ({
  fieldId,
  labelRegister,
  errors,
  onDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: fieldId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        p: 1,
        backgroundColor: "#FAFAFA",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#999",
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
            flexShrink: 0,
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CustomTextInput
            label=""
            name={`${fieldId}_label`}
            register={labelRegister}
            errors={errors}
            placeholder="項目名"
            hasSpacing={false}
            inputWidth="100%"
            inputWidthSp="100%"
          />
        </Box>
        <IconButton
          size="small"
          onClick={onDelete}
          title="削除"
          sx={{
            color: colors.text.secondary,
            flexShrink: 0,
            "&:hover": { color: colors.status.error },
          }}
        >
          <ButtonDeleteIcon width={14} height={16} />
        </IconButton>
      </Stack>
    </Box>
  );
};

const fieldToDraft = (f: UnitCustomField): DraftField => ({
  field_id: f.field_id,
  label: f.label,
  field_type: f.field_type,
  options: (f.options || []).join("\n"),
  dirty: false,
  section_key: f.section_key,
});

const normalizeDraftOptions = (options: string) =>
  options
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean)
    .join("\n");

const isDraftDirtyFromRecord = (
  draft: DraftField,
  record?: UnitCustomField,
) => {
  if (draft.isNew) return draft.label.trim().length > 0;
  if (!record) return draft.dirty;
  return (
    draft.label !== record.label ||
    draft.field_type !== record.field_type ||
    draft.section_key !== record.section_key ||
    normalizeDraftOptions(draft.options) !==
      normalizeDraftOptions((record.options || []).join("\n"))
  );
};

interface CustomFormSettingPageProps {
  /** カスタム設定ページのタブ内に埋め込む場合 true。自前の大見出し・上部余白を抑制する。 */
  embedded?: boolean;
}

const CustomFormSettingPage: React.FC<CustomFormSettingPageProps> = ({
  embedded = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const { data: items, loading, loaded } = useSelector(
    (s: RootState) => s.unitCustomFields.list,
  );

  const [formScopeIndex, setFormScopeIndex] = useState<number>(0);
  const [expandedFixed, setExpandedFixed] = useState<Record<string, boolean>>(
    {},
  );
  // 各セクションの開閉状態 (デフォルトは閉じる)
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(() => Object.fromEntries(SECTIONS.map((s) => [s.key, false])));
  const [drafts, setDrafts] = useState<Record<string, DraftField>>({});
  const [pendingDeleteFieldIds, setPendingDeleteFieldIds] = useState<string[]>(
    [],
  );
  const [footerDeleteConfirmOpen, setFooterDeleteConfirmOpen] = useState(false);
  const [footerSaving, setFooterSaving] = useState(false);

  // CustomTextInput / CustomSingleSelect 用のローカル useForm。
  // 各 input の name は `<field_id>_<key>` で動的に割り当てる。
  const editForm = useForm<Record<string, unknown>>({ defaultValues: {} });

  // dnd-kit センサー (5px 動かしてからドラッグ開始 → 通常クリックを邪魔しない)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // ドラッグ後の楽観 UI 用: section_key -> field_id 配列の並び順を保持。
  // BE への PATCH と再フェッチが完了するまでこの順序で表示し、ジャンプを防ぐ。
  const [orderOverride, setOrderOverride] = useState<Record<string, string[]>>(
    {},
  );

  const [initialLoadError, setInitialLoadError] =
    useState<ApiErrorType | null>(null);
  const hasCachedFieldData = loaded || items.length > 0;

  const fetchFields = useCallback(async () => {
    setInitialLoadError(null);
    const result = await dispatch(fetchUnitCustomFields());
    if (!fetchUnitCustomFields.fulfilled.match(result)) {
      const payload = result.payload as ApiErrorPayload | undefined;
      setInitialLoadError(payload?.errorType ?? "unknown");
    }
  }, [dispatch]);

  useEffect(() => {
    void fetchFields();
  }, [fetchFields]);

  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasCachedFieldData,
    dedupeKey: "custom-form",
  });

  useEffect(() => {
    setDrafts((prev) => {
      const next: Record<string, DraftField> = {};
      items.forEach((it) => {
        const existing = prev[it.field_id];
        next[it.field_id] =
          existing && isDraftDirtyFromRecord(existing, it)
            ? { ...existing, dirty: true }
            : fieldToDraft(it);
      });
      // ローカル新規 draft (isNew) は items 再取得で消えないように残す
      Object.values(prev).forEach((d) => {
        if (d.isNew) next[d.field_id] = d;
      });
      return next;
    });
  }, [items]);

  // form 初期値の同期: items / drafts (新規含む) のラベルを form の入力に流す
  useEffect(() => {
    Object.values(drafts).forEach((d) => {
      const labelKey = `${d.field_id}_label`;
      if (editForm.getValues(labelKey) !== d.label) {
        editForm.setValue(labelKey, d.label);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, drafts]);

  // form → drafts への同期 (CustomTextInput は register ベースで onChange prop が
  // 効かないため、watch を購読して draft.label に反映する)
  useEffect(() => {
    const sub = editForm.watch((values, info) => {
      const name = info?.name;
      if (!name || !name.endsWith("_label")) return;
      const fieldId = name.slice(0, -"_label".length);
      const v = values[name];
      if (typeof v !== "string") return;
      setDrafts((p) => {
        const cur = p[fieldId];
        if (!cur || cur.label === v) return p;
        const record = items.find((it) => it.field_id === fieldId);
        const next = { ...cur, label: v };
        return {
          ...p,
          [fieldId]: {
            ...next,
            dirty: isDraftDirtyFromRecord(next, record),
          },
        };
      });
    });
    return () => sub.unsubscribe();
  }, [editForm, items]);

  /** セクション別グルーピング (BE データのみ。ローカル新規 draft は別途末尾に表示する)。
   *  楽観 UI: orderOverride が設定されたセクションはその順序を優先。 */
  const grouped = useMemo(() => {
    const map: Record<string, UnitCustomField[]> = {};
    SECTIONS.forEach((s) => (map[s.key] = []));
    items.forEach((it) => {
      const key = it.section_key || "basic";
      if (!map[key]) map[key] = [];
      map[key].push(it);
    });
    Object.keys(map).forEach((k) => {
      const override = orderOverride[k];
      if (override && override.length > 0) {
        // override 内の順 → override に無いものは display_order の末尾
        const orderMap = new Map(override.map((id, idx) => [id, idx]));
        map[k].sort((a, b) => {
          const ai = orderMap.get(a.field_id);
          const bi = orderMap.get(b.field_id);
          if (ai !== undefined && bi !== undefined) return ai - bi;
          if (ai !== undefined) return -1;
          if (bi !== undefined) return 1;
          return (a.display_order ?? 999) - (b.display_order ?? 999);
        });
      } else {
        map[k].sort(
          (a, b) => (a.display_order ?? 999) - (b.display_order ?? 999),
        );
      }
    });
    return map;
  }, [items, orderOverride]);

  // 並び替えは「更新」ボタンとは独立で即時 PATCH するため、hasDirtyDrafts には含めない。
  const hasDirtyDrafts = useMemo(
    () =>
      pendingDeleteFieldIds.length > 0 ||
      Object.values(drafts).some((d) => {
        if (d.isNew) return d.label.trim().length > 0;
        return isDraftDirtyFromRecord(
          d,
          items.find((it) => it.field_id === d.field_id),
        );
      }),
    [drafts, items, pendingDeleteFieldIds],
  );

  const toggleFixed = (key: string) =>
    setExpandedFixed((p) => ({ ...p, [key]: !p[key] }));

  // ── ドラッグ並び替え (@dnd-kit) ─────────────────────────────────
  // BE 既存 + ローカル新規 (isNew) を混在させて並び替えできるようにする。
  // 並び替え後の挙動 (工事一覧のステータスと同じ方針):
  //   - 即座に orderOverride を更新して UI 反映 (描画はローカル優先)
  //   - BE 既存項目の display_order は background で PATCH (await しない / refetch しない)
  //   - 「更新」ボタンとは独立。hasDirtyDrafts には含めない
  //   - 失敗時のみ toast + refetch で巻き戻し
  //   - ローカル新規項目は drafts 内なので、保存 (persistDraft) 時に override の
  //     位置から display_order を算出する
  const handleDragEndForSection = (
    sectionKey: UnitCustomFieldSection,
    event: DragEndEvent,
  ) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // 現在の表示順 (BE + ローカル) を構築
    const beItems = (grouped[sectionKey] || []).filter(
      (it) => !pendingDeleteFieldIds.includes(it.field_id),
    );
    const localItems = Object.values(drafts).filter(
      (d) => d.isNew && d.section_key === sectionKey,
    );
    type MergedItem = { field_id: string; isLocal: boolean };
    const merged: MergedItem[] = [
      ...beItems.map((it) => ({ field_id: it.field_id, isLocal: false })),
      ...localItems.map((d) => ({ field_id: d.field_id, isLocal: true })),
    ];
    // 既存 override を適用して現在の表示順に合わせる
    const existingOverride = orderOverride[sectionKey];
    if (existingOverride && existingOverride.length > 0) {
      const orderMap = new Map(existingOverride.map((id, idx) => [id, idx]));
      merged.sort((a, b) => {
        const ai = orderMap.get(a.field_id);
        const bi = orderMap.get(b.field_id);
        if (ai !== undefined && bi !== undefined) return ai - bi;
        if (ai !== undefined) return -1;
        if (bi !== undefined) return 1;
        return 0;
      });
    }

    const oldIndex = merged.findIndex((it) => it.field_id === active.id);
    const newIndex = merged.findIndex((it) => it.field_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered: MergedItem[] = arrayMove(merged, oldIndex, newIndex);

    // 楽観 UI: 全体 (BE + ローカル) の順序を override に保存
    setOrderOverride((p) => ({
      ...p,
      [sectionKey]: reordered.map((r: MergedItem) => r.field_id),
    }));

    // 同セクションにローカル draft (isNew、空/未保存両方) があれば PATCH をスキップ。
    //   - draft の位置が BE 既存項目の display_order スロットを占有した状態で BE 更新
    //     すると、draft が discard / 別位置に動いた時に display_order がずれる。
    //   - 「更新」ボタン押下時の persistOrderChanges で draft 保存後に order をまとめて反映する。
    if (localItems.length > 0) return;

    // BE 既存項目だけ background で PATCH
    const beUpdates: { fieldId: string; newOrder: number }[] = [];
    reordered.forEach((it, idx) => {
      if (it.isLocal) return;
      const be = beItems.find((b) => b.field_id === it.field_id);
      if (!be) return;
      const newOrder = idx + 1;
      if (be.display_order === newOrder) return;
      beUpdates.push({ fieldId: it.field_id, newOrder });
    });
    if (beUpdates.length === 0) return;
    Promise.all(
      beUpdates.map(({ fieldId, newOrder }) =>
        dispatch(
          updateUnitCustomField({
            field_id: fieldId,
            body: { display_order: newOrder },
          }),
        ).unwrap(),
      ),
    ).catch((e) => {
      showToast({
        type: "error",
        message:
          (e as { message?: string })?.message ||
          "並び替えの保存に失敗しました",
      });
      void dispatch(fetchUnitCustomFields());
      setOrderOverride((p) => {
        const { [sectionKey]: _, ...rest } = p;
        return rest;
      });
    });
  };

  /** ローカルに新規 draft 行を追加するだけ (API は走らない)。
   *  ユーザがラベル等を入力 → 「保存」ボタンで初めて createUnitCustomField を呼ぶ。 */
  const handleAddCustomField = (sectionKey: UnitCustomFieldSection) => {
    const tempId = `__new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setDrafts((p) => ({
      ...p,
      [tempId]: {
        field_id: tempId,
        label: "",
        field_type: "text",
        options: "",
        dirty: true,
        isNew: true,
        section_key: sectionKey,
      },
    }));
  };

  const persistDraft = async (
    fieldId: string,
    draft: DraftField,
    opts?: { quiet?: boolean },
  ): Promise<boolean> => {
    if (!draft.label.trim()) {
      showToast({ type: "error", message: "項目名は必須です" });
      return false;
    }
    const optionsArr =
      draft.field_type === "select"
        ? draft.options
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean)
        : undefined;
    if (
      draft.field_type === "select" &&
      (!optionsArr || optionsArr.length === 0)
    ) {
      showToast({
        type: "error",
        message: "選択フィールドは選択肢が 1 件以上必要です",
      });
      return false;
    }

    if (draft.isNew) {
      const sectionKey = draft.section_key ?? "basic";
      const latestItems = store.getState().unitCustomFields.list.data;
      // ローカル項目を DnD で並び替えていた場合は override の位置から
      // display_order を算出する。並び替えていなければ末尾に追加。
      const sectionOverride = orderOverride[sectionKey];
      const overrideIdx =
        sectionOverride?.indexOf(fieldId) ?? -1;
      const nextOrder =
        overrideIdx >= 0
          ? overrideIdx + 1
          : latestItems
              .filter((it) => (it.section_key || "basic") === sectionKey)
              .reduce((m, it) => Math.max(m, it.display_order ?? 0), 0) + 1;
      const r = await dispatch(
        createUnitCustomField({
          label: draft.label.trim(),
          field_type: draft.field_type,
          options: optionsArr,
          section_key: sectionKey,
          display_order: nextOrder,
        }),
      );
      if (createUnitCustomField.fulfilled.match(r)) {
        setDrafts((p) => {
          const { [fieldId]: _, ...rest } = p;
          return rest;
        });
        if (!opts?.quiet) {
          showToast({ type: "success", message: "項目を追加しました" });
        }
        return true;
      }
      showToast({
        type: "error",
        message: (r.payload as string) || "追加に失敗しました",
      });
      return false;
    }

    const r = await dispatch(
      updateUnitCustomField({
        field_id: fieldId,
        body: {
          label: draft.label.trim(),
          field_type: draft.field_type,
          options: optionsArr,
        },
      }),
    );
    if (updateUnitCustomField.fulfilled.match(r)) {
      setDrafts((p) => ({ ...p, [fieldId]: { ...p[fieldId], dirty: false } }));
      if (!opts?.quiet) {
        showToast({ type: "success", message: "更新しました" });
      }
      return true;
    }
    showToast({
      type: "error",
      message: (r.payload as string) || "更新に失敗しました",
    });
    return false;
  };

  const handleSaveField = async (
    fieldId: string,
    opts?: { quiet?: boolean },
  ): Promise<boolean> => {
    const draft = drafts[fieldId];
    if (!draft) return false;
    return persistDraft(fieldId, draft, opts);
  };

  // orderOverride に基づいて BE 既存項目の display_order を一括 PATCH する。
  // (drafts が存在するときに handleDragEnd で skip した分を、save 時にまとめて反映する役割)
  const persistOrderChanges = async (): Promise<boolean> => {
    const latest = store.getState().unitCustomFields.list.data;
    for (const [sectionKey, ids] of Object.entries(orderOverride)) {
      if (!ids || ids.length === 0) continue;
      for (let idx = 0; idx < ids.length; idx++) {
        const fid = ids[idx];
        const be = latest.find(
          (it) => it.field_id === fid && it.section_key === sectionKey,
        );
        if (!be) continue;
        const targetOrder = idx + 1;
        if ((be.display_order ?? 0) === targetOrder) continue;
        const r = await dispatch(
          updateUnitCustomField({
            field_id: fid,
            body: { display_order: targetOrder },
          }),
        );
        if (!updateUnitCustomField.fulfilled.match(r)) {
          showToast({
            type: "error",
            message: (r.payload as string) || "並び替えの保存に失敗しました",
          });
          return false;
        }
      }
    }
    return true;
  };

  // ラベル未入力で空の新規行を drafts から取り除く。
  // BE には届かない (label 必須でエラー) ので、UI 上もリセット時に整理する。
  const dropEmptyNewDrafts = () => {
    setDrafts((p) => {
      const next: typeof p = {};
      for (const [k, v] of Object.entries(p)) {
        if (v.isNew && v.label.trim() === "") continue;
        next[k] = v;
      }
      return next;
    });
  };

  const runFooterSaveOnly = async () => {
    const ids = Object.keys(drafts).filter((id) => {
      const d = drafts[id];
      if (!d) return false;
      if (d.isNew) return d.label.trim().length > 0;
      return isDraftDirtyFromRecord(
        d,
        items.find((it) => it.field_id === id),
      );
    });
    if (ids.length === 0) return;
    setFooterSaving(true);
    try {
      for (const id of ids) {
        const ok = await handleSaveField(id, { quiet: true });
        if (!ok) return;
      }
      // draft 保存後、handleDragEnd で skip されていた order 変更をまとめて反映
      const orderOk = await persistOrderChanges();
      if (!orderOk) return;
      setOrderOverride({});
      dropEmptyNewDrafts();
      await dispatch(fetchUnitCustomFields());
      showToast({ type: "success", message: "更新しました" });
    } finally {
      setFooterSaving(false);
    }
  };

  const handleFooterUpdateClick = () => {
    if (pendingDeleteFieldIds.length > 0) {
      setFooterDeleteConfirmOpen(true);
      return;
    }
    void runFooterSaveOnly();
  };

  const confirmFooterDeleteAndSave = async () => {
    const toDelete = [...pendingDeleteFieldIds];
    const draftsSnapshot = { ...drafts };
    setFooterDeleteConfirmOpen(false);
    setFooterSaving(true);
    try {
      for (const fid of toDelete) {
        const r = await dispatch(deleteUnitCustomField({ field_id: fid }));
        if (!deleteUnitCustomField.fulfilled.match(r)) {
          showToast({
            type: "error",
            message: (r.payload as string) || "削除に失敗しました",
          });
          return;
        }
      }
      setPendingDeleteFieldIds([]);
      await dispatch(fetchUnitCustomFields());

      const toSaveIds = Object.keys(draftsSnapshot).filter((id) => {
        if (toDelete.includes(id)) return false;
        const d = draftsSnapshot[id];
        if (!d) return false;
        if (d.isNew) return d.label.trim().length > 0;
        return isDraftDirtyFromRecord(
          d,
          items.find((it) => it.field_id === id),
        );
      });

      for (const id of toSaveIds) {
        const d = draftsSnapshot[id];
        if (!d) continue;
        const ok = await persistDraft(id, d, { quiet: true });
        if (!ok) return;
      }

      // draft 保存後、handleDragEnd で skip されていた order 変更をまとめて反映
      const orderOk = await persistOrderChanges();
      if (!orderOk) return;

      if (toDelete.length > 0 || toSaveIds.length > 0) {
        setOrderOverride({});
        dropEmptyNewDrafts();
        await dispatch(fetchUnitCustomFields());
        showToast({ type: "success", message: "更新しました" });
      }
    } finally {
      setFooterSaving(false);
    }
  };

  const cancelFooterDeleteConfirm = () => setFooterDeleteConfirmOpen(false);

  const handleDeleteFieldClick = (fieldId: string) => {
    const draft = drafts[fieldId];
    if (draft?.isNew) {
      setDrafts((p) => {
        const { [fieldId]: _, ...rest } = p;
        return rest;
      });
      return;
    }
    setPendingDeleteFieldIds((prev) =>
      prev.includes(fieldId) ? prev : [...prev, fieldId],
    );
  };

  if (shouldShowInitialLoadError(initialLoadError, hasCachedFieldData)) {
    return (
      <InitialLoadError errorType={initialLoadError} onRetry={fetchFields} />
    );
  }

  return (
    <CustomFormNavigationGuard hasFormChanged={hasDirtyDrafts}>
      {() => (
    <Box pb={9} pt={embedded ? 0 : { xs: 2, sm: 3 }}>
      {!embedded && (
        <CustomSectionTitle title="カスタムフォーム" addBorder size="large" />
      )}
      <Box sx={{ mt: embedded ? 0 : 2, mb: 2 }}>
        <Tabs
          value={formScopeIndex}
          onChange={(_, v) => setFormScopeIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: "30px",
            borderBottom: `1px solid ${colors.border.tab}`,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              position: "relative",
              minHeight: "30px",
              paddingTop: 0.5,
              paddingBottom: 0.5,
              paddingLeft: 2,
              paddingRight: 2,
              minWidth: "auto",
              textTransform: "none",
              fontFamily: '"Noto Sans JP"',
              fontSize: "14px",
              fontWeight: 700,
              lineHeight: "1.7",
              color: colors.text.secondary,
              "&:hover": { color: colors.primary.main, opacity: 1 },
              "&.Mui-selected": {
                color: colors.primary.main,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "2px",
                  backgroundColor: colors.primary.main,
                  borderRadius: "10px 10px 0 0",
                },
              },
            },
            "& .MuiTabs-scrollButtons": {
              color: colors.primary.main,
              "&.Mui-disabled": { opacity: 0.3 },
            },
          }}
        >
          <Tab label="物件構成" />
          <Tab label="点検/清掃" />
          <Tab label="収支" />
        </Tabs>
      </Box>
      {formScopeIndex === 1 ? (
        <ManagementTaskCustomFormSection />
      ) : formScopeIndex === 2 ? (
        <ExpenseCustomFormSection />
      ) : (
        <>
      <Typography
        variant="body2"
        sx={{
          mb: 2,
          mt: 1,
          color: "#666",
          fontSize: { xs: 12, sm: 14 },
          lineHeight: 1.6,
        }}
      >
        詳細情報フォームに追加する項目を、セクションごとに管理します。
        固定項目はシステム既定で編集不可、追加項目はラベル・種別を編集できます。
      </Typography>

      <Box sx={{ mb: 1.5 }}>
        <CustomSectionTitle title="詳細情報" size="small" />
      </Box>

      {loading && !loaded ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
            width: "100%",
          }}
        >
          <CustomLoading loading={true} fullScreen={false} size="medium" />
        </Box>
      ) : (
      /* useFlexGap を有効化して CSS gap で余白を取る (Accordion の Mui-expanded 時の margin:0 影響を回避)。
          書類設定ページと同じく、リスト本体は md 以上で 60% 幅に制限 (タイトルはフル幅)。 */
      <Stack spacing={1.5} useFlexGap sx={{ width: { xs: "100%", md: "60%" } }}>
        {SECTIONS.map((section) => {
          // BE データの行 + ローカル新規 draft (isNew=true) を結合して表示
          const beFields = (grouped[section.key] || []).filter(
            (it) => !pendingDeleteFieldIds.includes(it.field_id),
          );
          const localNewDrafts = Object.values(drafts).filter(
            (d) => d.isNew && d.section_key === section.key,
          );
          // 表示用: 共通プロパティだけ取り出す軽量オブジェクト
          const customFields: Array<
            Pick<UnitCustomField, "field_id" | "label" | "field_type">
          > = [
            ...beFields,
            ...localNewDrafts.map((d) => ({
              field_id: d.field_id,
              label: d.label,
              field_type: d.field_type,
            })),
          ];
          // BE 項目とローカル項目を混在で並び替え可能にするため、
          // override が存在する場合はマージ後リストにも適用する。
          const sectionOverride = orderOverride[section.key];
          if (sectionOverride && sectionOverride.length > 0) {
            const orderMap = new Map(
              sectionOverride.map((id, idx) => [id, idx]),
            );
            customFields.sort((a, b) => {
              const ai = orderMap.get(a.field_id);
              const bi = orderMap.get(b.field_id);
              if (ai !== undefined && bi !== undefined) return ai - bi;
              if (ai !== undefined) return -1;
              if (bi !== undefined) return 1;
              return 0;
            });
          }
          const expanded = expandedSections[section.key] ?? false;
          return (
            <Accordion
              key={section.key}
              sx={ACCORDION_SX}
              expanded={expanded}
              onChange={(_, next) =>
                setExpandedSections((p) => ({ ...p, [section.key]: next }))
              }
            >
              <AccordionSummary
                // 標準アイコンは使わず、自前で左側に配置 (物件一覧と同じパターン)
                expandIcon={null}
                sx={ACCORDION_SUMMARY_SX}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  {/* 開閉アイコン (物件一覧と同スタイル) */}
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: expanded ? "#DBF8FF" : "transparent",
                      color: expanded ? "#0E9DBC" : "#3e3e3e",
                      transition: "all 0.2s",
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: "#DBF8FF",
                        "& svg": { color: "#0E9DBC" },
                      },
                    }}
                  >
                    <ChevronRightIcon
                      sx={{
                        transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                        fontSize: 20,
                        color: "inherit",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      gap: "8px",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 600, color: "#3E3E3E" }}
                    >
                      {section.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: colors.text.secondary,
                        backgroundColor: "#F5F5F5",
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        flexShrink: 0,
                      }}
                    >
                      {customFields.length}件
                    </Typography>
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ borderTop: "1px solid #E0E0E0", p: 2 }}>
                {/* 固定項目 */}
                {section.fixedFields.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Box
                      onClick={() => toggleFixed(section.key)}
                      sx={[
                        {
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          width: "fit-content",
                          maxWidth: "100%",
                          borderRadius: "8px",
                          px: 0.5,
                          py: 0.25,
                          transition: "color 0.2s",
                        },
                        expandedFixed[section.key]
                          ? {
                              color: "#0E9DBC",
                              "& svg": { color: "#0E9DBC" },
                            }
                          : {
                              color: "#3E3E3E",
                              "&:hover": {
                                color: "#0E9DBC",
                                "& svg": { color: "#0E9DBC" },
                              },
                            },
                      ]}
                    >
                      {expandedFixed[section.key] ? (
                        <ExpandMoreIcon
                          sx={{ fontSize: 14, color: "inherit" }}
                        />
                      ) : (
                        <ChevronRightIcon
                          sx={{ fontSize: 14, color: "inherit" }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: "inherit",
                        }}
                      >
                        固定項目 ({section.fixedFields.length}件)
                      </Typography>
                    </Box>
                    <Collapse in={!!expandedFixed[section.key]}>
                      <Stack spacing={0.5}>
                        {section.fixedFields.map((f) => (
                          <Box
                            key={f.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1,
                              py: 0.75,
                              fontSize: 12,
                              backgroundColor: "rgba(0,0,0,0.02)",
                              borderRadius: 1,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>{f.label}</Box>
                            {f.required && (
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: "#DA384C",
                                  fontWeight: 700,
                                }}
                              >
                                必須
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </Collapse>
                  </Box>
                )}

                {/* 追加項目 */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#3E3E3E",
                      mb: 1,
                    }}
                  >
                    追加項目
                  </Typography>
                  {customFields.length > 0 && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      // 縦軸のみ + 親 (リスト) 内に制限
                      modifiers={[
                        restrictToVerticalAxis,
                        restrictToParentElement,
                      ]}
                      onDragEnd={(event: DragEndEvent) =>
                        handleDragEndForSection(section.key, event)
                      }
                    >
                      <SortableContext
                        // BE 既存行 + ローカル新規 (isNew) も並び替え対象に含める。
                        items={customFields.map((cf) => cf.field_id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {/* useFlexGap: dnd-kit の transform/transition と
                            Stack の margin 計算が競合してジャンプするのを防ぐ */}
                        <Stack spacing={1} useFlexGap sx={{ mb: 1 }}>
                          {customFields.map((f) => {
                            const draft = drafts[f.field_id];
                            if (!draft) return null;
                            return (
                              <SortableFieldRow
                                key={f.field_id}
                                fieldId={f.field_id}
                                labelRegister={editForm.register}
                                errors={editForm.formState.errors}
                                onDelete={() =>
                                  handleDeleteFieldClick(f.field_id)
                                }
                              />
                            );
                          })}
                        </Stack>
                      </SortableContext>
                    </DndContext>
                  )}
                  {/* 接道追加と同じ clearPrimary + dashed の追加ボタン */}
                  <Can action="config:edit">
                    <CustomButton
                      label="新しい項目を追加"
                      buttonCategory="clearPrimary"
                      onClick={() => handleAddCustomField(section.key)}
                      sx={{ width: "100%", borderStyle: "dashed" }}
                    />
                  </Can>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
      )}

      <CustomModal
        openModal={footerDeleteConfirmOpen}
        handleCloseModal={cancelFooterDeleteConfirm}
        title="項目を削除しますか？"
        modalType="deleteModal"
        bodyText=""
        hideCloseButton
      >
        <Box>
          <Typography sx={{ fontSize: 14, color: "#666" }}>
            削除すると、全ての区画でこの項目の入力内容が削除されます。
            <br />
            この操作は取り消せません。削除してよろしいですか？
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <CustomButton
            label="戻る"
            onClick={cancelFooterDeleteConfirm}
            buttonCategory="cancelModal"
          />
          <Can action="config:edit">
            <CustomButton
              label="削除する"
              onClick={() => void confirmFooterDeleteAndSave()}
              buttonCategory="deleteModal"
            />
          </Can>
        </Box>
      </CustomModal>

      <Can action="config:edit">
        <FixedActionBar
          submitLabel="更新"
          isLoading={footerSaving}
          disabled={!hasDirtyDrafts || footerSaving}
          onSubmit={() => void handleFooterUpdateClick()}
        />
      </Can>
        </>
      )}
    </Box>
      )}
    </CustomFormNavigationGuard>
  );
};

export default CustomFormSettingPage;
